/*
 * pdp-social-proof.js
 * --------------------------------------------------------------------------
 * Customer video cards + dynamic carousel.
 *
 * - No fixed card count. Every behaviour reads the cards that actually exist.
 * - Featured homepage mode: one enlarged active card on desktop, matching the
 *   supplied HTML reference; arrows wrap through any number of blocks.
 * - Mobile/tablet: equal-width native swipe cards for lighter touch behaviour.
 * - Videos stay preload="none" and only start after a real click.
 * - Existing category filters, progress bar and product add action are kept.
 * - No dependency / carousel library.
 * --------------------------------------------------------------------------
 */

(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 990px)');

  function sections() {
    return Array.from(document.querySelectorAll('.pdp-proof'));
  }

  function cards(section) {
    return Array.from(section.querySelectorAll('[data-proof-card]')).filter(function (card) {
      return !card.hidden;
    });
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function pauseOtherVideos(current) {
    document.querySelectorAll('[data-proof-video]').forEach(function (video) {
      if (video === current) return;
      if (!video.paused) video.pause();
      var media = video.closest('.pdp-proof__media');
      if (media) media.classList.remove('is-playing');
    });
  }

  /* -----------------------------------------------------------------------
     Video playback
     ----------------------------------------------------------------------- */

  function bindVideoButton(button) {
    if (button.dataset.proofVideoReady === 'true') return;
    button.dataset.proofVideoReady = 'true';

    var media = button.closest('.pdp-proof__media');
    if (!media) return;

    var video = media.querySelector('[data-proof-video]');
    if (!video) return;

    button.addEventListener('click', function () {
      var section = button.closest('.pdp-proof');
      var card = button.closest('[data-proof-card]');

      if (section && card && section.hasAttribute('data-proof-featured')) {
        activateFeaturedCard(section, card, true);
      }

      pauseOtherVideos(video);
      media.classList.add('is-playing');
      video.setAttribute('controls', 'controls');

      var started = video.play();
      if (started && typeof started.then === 'function') {
        started.then(function () {
          video.muted = false;
        }).catch(function () {
          media.classList.remove('is-playing');
        });
      } else {
        video.muted = false;
      }
    });

    video.addEventListener('ended', function () {
      media.classList.remove('is-playing');
      video.removeAttribute('controls');
      video.currentTime = 0;
    });
  }

  /* -----------------------------------------------------------------------
     Featured homepage carousel
     ----------------------------------------------------------------------- */

  function featuredIndex(section) {
    var list = cards(section);
    if (!list.length) return -1;

    var active = list.findIndex(function (card) {
      return card.classList.contains('is-active');
    });

    return active >= 0 ? active : 0;
  }

  function centerFeaturedCard(section, card, behavior) {
    var track = section.querySelector('[data-proof-track]');
    if (!track || !card) return;

    /* On desktop the active card is enlarged. Native scroll keeps this robust
       whether there are 3 cards or 30; the browser supplies the end clamps. */
    var left = card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
    var max = Math.max(0, track.scrollWidth - track.clientWidth);

    track.scrollTo({
      left: clamp(left, 0, max),
      behavior: behavior || 'smooth'
    });
  }

  function activateFeaturedCard(section, cardOrIndex, shouldCenter) {
    if (!section || !section.hasAttribute('data-proof-featured')) return;

    var list = cards(section);
    if (!list.length) return;

    var index;
    if (typeof cardOrIndex === 'number') {
      index = cardOrIndex;
    } else {
      index = list.indexOf(cardOrIndex);
    }

    if (index < 0) index = 0;
    index = ((index % list.length) + list.length) % list.length;

    list.forEach(function (card, i) {
      var on = i === index;
      card.classList.toggle('is-active', on);
      if (on) {
        card.setAttribute('aria-current', 'true');
      } else {
        card.removeAttribute('aria-current');
      }
    });

    section.dataset.proofActiveIndex = String(index);

    var active = list[index];
    if (!shouldCenter) return;

    /* First centre immediately, then once more after the width/height
       transition has settled. That keeps the large card accurately placed
       even when its width changes from 214px to 340px. */
    requestAnimationFrame(function () {
      centerFeaturedCard(section, active, 'smooth');
    });

    window.setTimeout(function () {
      centerFeaturedCard(section, active, 'smooth');
    }, 410);
  }

  function updateFeaturedNav(section) {
    var nav = section.querySelector('[data-proof-nav]');
    if (!nav) return;
    nav.hidden = cards(section).length <= 1;
  }

  function bindFeatured(section) {
    if (!section.hasAttribute('data-proof-featured')) return;
    if (section.dataset.proofFeaturedReady === 'true') {
      updateFeaturedNav(section);
      return;
    }

    section.dataset.proofFeaturedReady = 'true';

    var track = section.querySelector('[data-proof-track]');
    var prev = section.querySelector('[data-proof-prev]');
    var next = section.querySelector('[data-proof-next]');
    if (!track) return;

    function initialIndex() {
      var list = cards(section);
      if (!list.length) return 0;

      /* Liquid renders the starting active card server-side so the enlarged
         card is already visible before JavaScript finishes loading. Reuse it
         here instead of briefly resetting the rail. */
      var rendered = list.findIndex(function (card) {
        return card.classList.contains('is-active');
      });
      if (rendered >= 0) return rendered;

      /* Fallback for older saved markup / dynamically injected sections. */
      if (!DESKTOP.matches) return 0;

      var requested = parseInt(section.getAttribute('data-proof-start-card'), 10);
      if (!Number.isFinite(requested)) requested = 1;
      return clamp(requested - 1, 0, list.length - 1);
    }

    activateFeaturedCard(section, initialIndex(), DESKTOP.matches);
    updateFeaturedNav(section);

    track.addEventListener('click', function (event) {
      var card = event.target.closest('[data-proof-card]');
      if (!card || !track.contains(card)) return;

      /* Links/buttons/videos keep their own action. Play explicitly activates
         the card in bindVideoButton before starting the clip. */
      if (event.target.closest('a, button, video')) return;

      activateFeaturedCard(section, card, true);
    });

    track.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.closest('a, button, video')) return;

      event.preventDefault();
      var delta = event.key === 'ArrowRight' ? 1 : -1;
      activateFeaturedCard(section, featuredIndex(section) + delta, true);
    });

    if (prev) {
      prev.addEventListener('click', function () {
        activateFeaturedCard(section, featuredIndex(section) - 1, true);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        activateFeaturedCard(section, featuredIndex(section) + 1, true);
      });
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        var index = featuredIndex(section);
        if (!DESKTOP.matches && index < 0) index = 0;
        activateFeaturedCard(section, Math.max(0, index), DESKTOP.matches);
        updateFeaturedNav(section);
      }, 100);
    });
  }

  /* -----------------------------------------------------------------------
     Standard / Chapter navigation
     ----------------------------------------------------------------------- */

  function updateStandardNav(section) {
    if (section.hasAttribute('data-proof-featured')) return;

    var track = section.querySelector('[data-proof-track]');
    var nav = section.querySelector('[data-proof-nav]');
    if (!track || !nav) return;

    nav.hidden = cards(section).length <= 1 || track.scrollWidth <= track.clientWidth + 2;
  }

  function bindStandardNav(section) {
    if (section.hasAttribute('data-proof-featured')) return;
    if (section.dataset.proofNavReady === 'true') {
      updateStandardNav(section);
      return;
    }

    section.dataset.proofNavReady = 'true';

    var track = section.querySelector('[data-proof-track]');
    var prev = section.querySelector('[data-proof-prev]');
    var next = section.querySelector('[data-proof-next]');
    if (!track) return;

    function amount() {
      var list = cards(section);
      if (!list.length) return track.clientWidth * 0.8;
      var first = list[0];
      var second = list[1];
      if (second) return Math.abs(second.offsetLeft - first.offsetLeft);
      return first.getBoundingClientRect().width + 16;
    }

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -amount(), behavior: 'smooth' });
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        track.scrollBy({ left: amount(), behavior: 'smooth' });
      });
    }

    window.addEventListener('resize', function () {
      updateStandardNav(section);
    });

    updateStandardNav(section);
  }

  /* -----------------------------------------------------------------------
     Scroll progress
     ----------------------------------------------------------------------- */

  function updateProgress(section) {
    var track = section.querySelector('[data-proof-track]');
    var fill = section.querySelector('[data-proof-progress-fill]');
    if (!track || !fill) return;

    var span = track.scrollWidth - track.clientWidth;
    var ratio = span > 1 ? track.scrollLeft / span : 1;
    fill.style.width = clamp(ratio, 0, 1) * 100 + '%';
  }

  function bindProgress(section) {
    var track = section.querySelector('[data-proof-track]');
    var fill = section.querySelector('[data-proof-progress-fill]');
    if (!track || !fill || track.dataset.proofProgressReady === 'true') return;

    track.dataset.proofProgressReady = 'true';
    track.addEventListener('scroll', function () {
      updateProgress(section);
    }, { passive: true });
    window.addEventListener('resize', function () {
      updateProgress(section);
    });
    updateProgress(section);
  }

  /* -----------------------------------------------------------------------
     Category filter
     ----------------------------------------------------------------------- */

  function bindJobs(section) {
    var bar = section.querySelector('[data-proof-jobs]');
    var track = section.querySelector('[data-proof-track]');
    if (!bar || !track || bar.dataset.proofJobsReady === 'true') return;

    bar.dataset.proofJobsReady = 'true';
    var thumb = bar.querySelector('[data-proof-thumb]');

    function placeThumb(instant) {
      var on = bar.querySelector('[data-proof-job].is-on');
      if (!thumb || !on) return;

      if (instant) thumb.style.transition = 'none';
      var barBox = bar.getBoundingClientRect();
      var onBox = on.getBoundingClientRect();
      thumb.style.width = onBox.width + 'px';
      thumb.style.transform = 'translateX(' + (onBox.left - barBox.left + bar.scrollLeft) + 'px)';
      bar.classList.add('has-thumb');

      if (instant) {
        thumb.offsetWidth;
        thumb.style.transition = '';
      }
    }

    placeThumb(true);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { placeThumb(true); });
    }

    window.addEventListener('resize', function () {
      placeThumb(true);
    });

    bar.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-proof-job]');
      if (!tab) return;

      var wanted = tab.getAttribute('data-proof-job');

      bar.querySelectorAll('[data-proof-job]').forEach(function (item) {
        var on = item === tab;
        item.classList.toggle('is-on', on);
        item.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      placeThumb(false);

      var left = tab.offsetLeft - (bar.clientWidth - tab.offsetWidth) / 2;
      bar.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });

      track.querySelectorAll('[data-proof-card]').forEach(function (card) {
        card.hidden = wanted !== '' && card.getAttribute('data-job') !== wanted;
      });

      track.scrollLeft = 0;
      updateProgress(section);
      updateStandardNav(section);
    });
  }

  /* -----------------------------------------------------------------------
     Optional add-to-cart action in the product strip
     ----------------------------------------------------------------------- */

  function bindAddButton(button) {
    if (button.dataset.proofAddReady === 'true') return;
    button.dataset.proofAddReady = 'true';

    button.addEventListener('click', function () {
      if (button.disabled) return;

      var id = parseInt(button.getAttribute('data-variant-id'), 10);
      if (!id) return;

      button.disabled = true;

      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart request failed');
          return response.json();
        })
        .then(function (data) {
          button.classList.add('is-added');
          button.textContent = '✓';
          button.setAttribute('aria-label', 'Added to cart');
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        })
        .catch(function () {
          button.disabled = false;
        });
    });
  }

  /* -----------------------------------------------------------------------
     Init / Shopify editor lifecycle
     ----------------------------------------------------------------------- */

  function initSection(section) {
    section.querySelectorAll('[data-proof-play]').forEach(bindVideoButton);
    section.querySelectorAll('[data-proof-add]').forEach(bindAddButton);

    bindFeatured(section);
    bindStandardNav(section);
    bindProgress(section);
    bindJobs(section);
  }

  function init(root) {
    var scope = root || document;

    if (scope.matches && scope.matches('.pdp-proof')) {
      initSection(scope);
      return;
    }

    scope.querySelectorAll('.pdp-proof').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target.closest ? event.target.closest('[data-proof-card]') : null;
    if (!card) return;

    var section = card.closest('.pdp-proof[data-proof-featured]');
    if (!section) return;

    activateFeaturedCard(section, card, true);
  });
})();

/* ========================================================================
   Featured-video reference carousel
   - Dynamic active card on desktop
   - Native swipe cards on mobile/tablet
   - Optional category tabs filter the visible customer videos
   - Uses each block's existing "Category (tab)" value
   ======================================================================== */
(function () {
  'use strict';

  function numberAttr(section, name, fallback) {
    var n = parseFloat(section.getAttribute(name));
    return Number.isFinite(n) ? n : fallback;
  }

  function activateMedia(card) {
    var video = card && card.querySelector('[data-fvr-video]');
    if (!video) return;

    document.querySelectorAll('[data-fvr-video]').forEach(function (other) {
      if (other === video) return;
      if (!other.paused) other.pause();
      var otherMedia = other.closest('.fvr__media');
      if (otherMedia) otherMedia.classList.remove('is-playing');
    });

    if (video.dataset.fvrLoaded !== 'true') {
      video.querySelectorAll('source[data-src]').forEach(function (source) {
        source.src = source.dataset.src;
      });
      video.load();
      video.dataset.fvrLoaded = 'true';
    }

    var media = video.closest('.fvr__media');
    if (media) media.classList.add('is-playing');
    video.setAttribute('controls', 'controls');

    var started = video.play();
    if (started && typeof started.catch === 'function') {
      started.catch(function () {
        if (media) media.classList.remove('is-playing');
      });
    }
  }

  function setup(section) {
    if (!section || section.dataset.fvrReady === 'true') return;

    var track = section.querySelector('[data-fvr-track]');
    var wrap = section.querySelector('[data-fvr-viewport]');
    if (!track || !wrap) return;

    var allCards = Array.prototype.slice.call(track.querySelectorAll('[data-fvr-card]'));
    if (!allCards.length) return;

    section.dataset.fvrReady = 'true';

    var BIG = numberAttr(section, 'data-fvr-big', 340);
    var SMALL = numberAttr(section, 'data-fvr-small', 214);
    var GAP = numberAttr(section, 'data-fvr-gap', 18);
    var BIG_MEDIA = numberAttr(section, 'data-fvr-big-media', 480);
    var SMALL_MEDIA = numberAttr(section, 'data-fvr-small-media', 366);
    var start = Math.round(numberAttr(section, 'data-fvr-start', 3));

    var prev = section.querySelector('[data-fvr-prev]');
    var next = section.querySelector('[data-fvr-next]');
    var nav = section.querySelector('.fvr__nav');
    var tabs = section.querySelector('[data-fvr-tabs]');
    var desktop = window.matchMedia('(min-width: 990px)');

    var cards = [];
    var sel = 0;

    function visibleCards() {
      return allCards.filter(function (card) {
        return !card.hidden;
      });
    }

    function updateNav() {
      if (!nav) return;
      nav.hidden = cards.length <= 1;
    }

    function resetHiddenCard(card) {
      card.classList.remove('is-active');
      card.removeAttribute('aria-current');
      card.style.removeProperty('flex');
      card.style.removeProperty('width');
      card.style.removeProperty('gap');

      var media = card.querySelector('[data-fvr-media]');
      if (media) {
        media.style.removeProperty('height');
        media.style.removeProperty('box-shadow');
      }

      var play = card.querySelector('[data-fvr-play]');
      if (play) {
        play.style.removeProperty('width');
        play.style.removeProperty('height');
      }
    }

    function apply(instant) {
      cards = visibleCards();

      allCards.forEach(function (card) {
        if (card.hidden) resetHiddenCard(card);
      });

      if (!cards.length) {
        track.style.transform = '';
        updateNav();
        return;
      }

      sel = Math.max(0, Math.min(sel, cards.length - 1));

      cards.forEach(function (card, i) {
        var on = i === sel;
        card.classList.toggle('is-active', on);

        if (on) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');

        if (!desktop.matches) {
          card.style.removeProperty('flex');
          card.style.removeProperty('width');
          card.style.removeProperty('gap');

          var mobileMedia = card.querySelector('[data-fvr-media]');
          if (mobileMedia) {
            mobileMedia.style.removeProperty('height');
            mobileMedia.style.removeProperty('box-shadow');
          }

          var mobilePlay = card.querySelector('[data-fvr-play]');
          if (mobilePlay) {
            mobilePlay.style.removeProperty('width');
            mobilePlay.style.removeProperty('height');
          }

          return;
        }

        card.style.flex = '0 0 ' + (on ? BIG : SMALL) + 'px';
        card.style.width = (on ? BIG : SMALL) + 'px';
        card.style.gap = '10px';

        var media = card.querySelector('[data-fvr-media]');
        if (media) {
          media.style.height = (on ? BIG_MEDIA : SMALL_MEDIA) + 'px';
          media.style.boxShadow = on
            ? '0 24px 48px -18px rgba(0,0,0,.45)'
            : '0 8px 20px -14px rgba(0,0,0,.35)';
        }

        var play = card.querySelector('[data-fvr-play]');
        if (play) {
          play.style.width = (on ? 74 : 56) + 'px';
          play.style.height = (on ? 74 : 56) + 'px';
        }
      });

      updateNav();

      if (!desktop.matches) {
        track.style.transform = '';
        track.style.gap = '';
        return;
      }

      var widths = cards.map(function (_, i) {
        return i === sel ? BIG : SMALL;
      });

      var before = 0;
      for (var i = 0; i < sel; i++) {
        before += widths[i] + GAP;
      }

      var total = widths.reduce(function (a, b) {
        return a + b;
      }, 0) + GAP * Math.max(0, widths.length - 1);

      var w = wrap.clientWidth;
      var x = w / 2 - (before + widths[sel] / 2);

      x = Math.min(0, Math.max(x, w - total));
      if (total < w) x = 0;

      if (instant) track.classList.add('fvr-no-transition');
      track.style.transform = 'translateX(' + x + 'px)';
      track.style.gap = GAP + 'px';

      if (instant) {
        void track.offsetWidth;
        requestAnimationFrame(function () {
          track.classList.remove('fvr-no-transition');
        });
      }
    }

    function choose(i) {
      cards = visibleCards();
      if (!cards.length) return;
      sel = (i + cards.length) % cards.length;
      apply(false);
    }

    function filterTo(wanted, instant) {
      allCards.forEach(function (card) {
        var matches = wanted === '' || card.getAttribute('data-job') === wanted;
        card.hidden = !matches;

        if (!matches) {
          var video = card.querySelector('[data-fvr-video]');
          if (video && !video.paused) video.pause();

          var media = card.querySelector('[data-fvr-media]');
          if (media) media.classList.remove('is-playing');
        }
      });

      cards = visibleCards();
      sel = 0;

      wrap.scrollLeft = 0;
      track.style.transform = '';

      apply(instant !== false);
    }

    function activateTab(tab, instant) {
      if (!tabs || !tab) return;

      tabs.querySelectorAll('[data-fvr-tab]').forEach(function (item) {
        var on = item === tab;
        item.classList.toggle('is-active', on);
        item.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      var wanted = tab.getAttribute('data-fvr-tab') || '';
      filterTo(wanted, instant);

      var left = tab.offsetLeft - (tabs.clientWidth - tab.offsetWidth) / 2;
      tabs.scrollTo({
        left: Math.max(0, left),
        behavior: instant ? 'auto' : 'smooth'
      });
    }

    allCards.forEach(function (card) {
      card.addEventListener('click', function (event) {
        if (card.hidden || event.target.closest('a')) return;

        cards = visibleCards();
        var index = cards.indexOf(card);
        if (index < 0) return;

        sel = index;
        apply(false);
      });

      var play = card.querySelector('[data-fvr-play]');
      if (play) {
        play.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();

          cards = visibleCards();
          var index = cards.indexOf(card);
          if (index < 0) return;

          sel = index;
          apply(false);
          activateMedia(card);
        });
      }

      var video = card.querySelector('[data-fvr-video]');
      if (video) {
        video.addEventListener('ended', function () {
          var media = video.closest('.fvr__media');
          if (media) media.classList.remove('is-playing');
          video.removeAttribute('controls');
          video.currentTime = 0;
        });
      }
    });

    if (tabs) {
      tabs.addEventListener('click', function (event) {
        var tab = event.target.closest('[data-fvr-tab]');
        if (!tab) return;
        activateTab(tab, false);
      });
    }

    if (prev) {
      prev.addEventListener('click', function () {
        choose(sel - 1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        choose(sel + 1);
      });
    }

    var initialTab = tabs && tabs.querySelector('[data-fvr-tab].is-active');

    if (initialTab) {
      var initialWanted = initialTab.getAttribute('data-fvr-tab') || '';
      allCards.forEach(function (card) {
        card.hidden = initialWanted !== '' && card.getAttribute('data-job') !== initialWanted;
      });
      cards = visibleCards();
      sel = initialWanted === ''
        ? Math.max(0, Math.min(cards.length - 1, start - 1))
        : 0;
    } else {
      cards = visibleCards();
      sel = Math.max(0, Math.min(cards.length - 1, start - 1));
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        apply(true);
      }, 80);
    });

    if (desktop.addEventListener) {
      desktop.addEventListener('change', function () {
        apply(true);
      });
    }

    apply(true);

    window.addEventListener('load', function () {
      apply(true);
    }, { once: true });
  }

  function init(root) {
    (root || document).querySelectorAll('[data-fvr]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target.closest && event.target.closest('[data-fvr-card]');
    if (!card) return;

    var section = card.closest('[data-fvr]');
    if (!section) return;

    var tabs = section.querySelector('[data-fvr-tabs]');
    var job = card.getAttribute('data-job') || '';

    if (card.hidden && tabs) {
      var targetTab = Array.prototype.slice.call(
        tabs.querySelectorAll('[data-fvr-tab]')
      ).find(function (tab) {
        return (tab.getAttribute('data-fvr-tab') || '') === job;
      });

      if (!targetTab) {
        targetTab = tabs.querySelector('[data-fvr-tab=""]');
      }

      if (targetTab) targetTab.click();
    }

    card.click();
  });
})();
